import { memo, useEffect } from 'react';

import { type LeadFormTrigger, useTrackOpenedPlansModal } from '@air/analytics';
import { Modal } from '@air/primitive-modal';
import { Spinner } from '@air/primitive-spinner';
import { useToasts } from '@air/provider-toast';
import { reportErrorToBugsnag } from '@air/utils-error';

import { DemoFormModal } from '~/components/Modals/DemoFormModal';
import { UpgradePlanModalContent } from '~/components/Modals/PlansModal/UpgradePlanModalContent';
import { usePlansList } from '~/hooks/usePlansList';
import { type EnhancedSubscription } from '~/providers/SubscriptionProvider';
import { useSubscriptionPlan } from '~/swr-hooks/subscriptions/useSubscriptionPlan';

export type UpgradePlanModalProps = {
  leadFormTrigger?: LeadFormTrigger;
  subscription: EnhancedSubscription;
};

export const UpgradePlanModal = memo(
  ({ leadFormTrigger, onClose, subscription }: AirModalProps<UpgradePlanModalProps>) => {
    const { data, error, isLoading: isPlansListLoading } = usePlansList();
    const { data: subscriptionPlan, isLoading: isSubscriptionPlanLoading } = useSubscriptionPlan();
    const { trackOpenedPlansModal } = useTrackOpenedPlansModal();
    const { showToast } = useToasts();

    useEffect(() => {
      if (error) {
        reportErrorToBugsnag({ error, context: 'Failed to fetch plans from PaidPlans modal' });
        showToast('Something went wrong. Please try again later.');
        onClose();
      }
    }, [error, onClose, showToast]);

    useEffect(() => {
      trackOpenedPlansModal();
    }, [trackOpenedPlansModal]);

    // Plans that don't self-serve an upgrade get the sales form instead of the plans grid. Demo
    // belongs here via `isOnEnterprisePlan`: `useSubscriptionPlan` is disabled for demo (the
    // endpoint throws `PLAN_NOT_FOUND`), so the grid would render with no current plan to compare
    // against — and the credit banner's "Talk to Sales" button routes here.
    if (subscription.isOnBusinessPlan || subscription.isOnEnterprisePlan) {
      return <DemoFormModal trigger={leadFormTrigger} onClose={onClose} />;
    }

    return (
      <Modal
        dangerouslyBypassFocusLock
        isOpen
        onDismiss={onClose}
        className="mt-0 w-full max-w-[1200px] p-0 lg:mt-8"
        overlayClassName="py-0 sm:py-8 sm:px-8 lg:px-0"
      >
        {isPlansListLoading || isSubscriptionPlanLoading ? (
          <div className="flex flex-col justify-center border-t ads:border-t-ads-gray-5 border-t-gray-5 sm:flex-row">
            <div className="flex min-h-[556px] flex-1 items-center justify-center">
              <Spinner className="ads:text-ads-cyan-10 text-teal-9" />
            </div>
          </div>
        ) : (
          <UpgradePlanModalContent
            plans={data?.plans ?? []}
            currentPlan={subscriptionPlan?.plan}
            subscription={subscription}
            onClose={onClose}
          />
        )}
      </Modal>
    );
  },
);

UpgradePlanModal.displayName = 'UpgradePlanModal';
